import { Component, ElementRef, NgZone, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { AnalyticsService } from '../../services/analytics.service';
import { LanguageService } from '../../services/language.service';
import { PromotionsService } from '../../services/promotions.service';
import { PromotionMediaItem, ScheduledPromotionDoc } from '../../models/promotion.model';
import { isVideoMedia } from '../../events/event-utils';
import { EventMediaComponent } from '../../events/components/event-media.component';
import {
  isMediaPromotion,
  mediaBackdropUrl,
  mediaFitFor,
  mediaOrientation,
  promotionMedia,
} from '../promotion-utils';

// --- Story pacing and tap geometry. The thresholds are the reel player's, verbatim, because a
// diner who has used the Feather Reel must not have to learn a second set of gestures for the
// promotion that advertised it. @see ReelOverlayComponent.
//
// STILLS ONLY. A clip paces itself and reports its own end, so nothing here applies to one — five
// seconds would cut a thirty-second video off at the knees.
const STILL_DURATION_MS = 5000;
const HOLD_THRESHOLD_MS = 180;
const TAP_PREV_MAX = 0.35;
const TAP_NEXT_MIN = 0.65;

/**
 * How far a pointer may travel and still count as a tap.
 *
 * Everything above this is the diner swiping, and a swipe belongs to Swiper — the track is
 * draggable and always has been. Without this the tap handler would fire `next()` at the end of
 * every swipe and the gallery would jump two slides at once.
 */
const TAP_SLOP_PX = 10;

/** Chrome that owns its own taps: a tap here must never also advance the story. */
const CHROME_SELECTOR = '.pm-top, .pm-pills, .pm-bars, .pm-footer, button, a';

/** Roughly two thirds of the stage: enough to read in, never so much that the media is forgotten. */
const READING_PANEL_MAX_VH = 62;

/**
 * Where a `type: "media"` promotion notification lands.
 *
 * THE NOTIFICATION ITSELF CAN NEVER SHOW THIS. The tray is browser UI, not ours: it gives one
 * static `image` and nothing else — no video, no carousel, no custom layout — and even that single
 * image is Chrome/Android only. So the shape of the feature is fixed: hero still in the tray,
 * real gallery on tap. This screen is the "on tap" half.
 *
 * Reached with only a promotion id (the Cloud Function's redirect path is `promotion/<docId>`), so
 * it always fetches — the gallery is deliberately not in the push payload, which caps at 4KB. See
 * `PromotionsService` for that trade in full.
 *
 * LAYOUT IS "MEDIA-FORWARD" AND WEB-ONLY. The media runs edge to edge and floor to ceiling, the
 * text sits on a scrim over it, and the controls float as glass pills. iOS keeps its contained
 * card, notched chip and 60vh gallery; the two platforms diverge here by decision. What does NOT
 * diverge is the content model — same documents, same fields, same analytics — so an iOS port
 * later needs no change on the server.
 *
 * Each slide is still `app-event-media`, the component the events feature renders its hero with:
 * a promotion's `mediaItems` is the same shape as an event's, and that component already handles
 * image-vs-video, posters, autoplay under the muted/playsinline/reduced-motion rules, and falling
 * back to a still on a dead URL. It gained one input for this screen — `muted` — because sound
 * here is opt-in from a tap.
 */
@Component({
    selector: 'app-promotion-details',
    templateUrl: './promotion-details.component.html',
    styleUrls: ['./promotion-details.component.scss'],
    standalone: false
})
export class PromotionDetailsComponent implements OnInit, OnDestroy {
  promotion: ScheduledPromotionDoc | null = null;
  /** The renderable gallery items, resolved once per load so the template stays declarative. */
  mediaItems: PromotionMediaItem[] = [];

  loading = true;
  notFound = false;

  /** The venue whose menu this session is on, for the chip over the media. */
  shopName = '';
  shopLogoUrl = '';

  /**
   * Whether the story is held still, which a press-and-hold does exactly as it does in the reel.
   * Read by the template so the bar's fill animation freezes with it rather than running on
   * against a slide that is no longer advancing.
   */
  paused = false;

  /**
   * Whether the copy is open in its scrollable reading panel.
   *
   * The media stays full-bleed behind it — this is a panel, not a second layout. While it is open
   * the story is held, because advancing a slide out from under someone reading twenty lines is
   * the most hostile thing this screen could do.
   */
  textExpanded = false;

  /**
   * Whether the collapsed copy is hiding anything, so "Read more" is offered only when it leads
   * somewhere. Measured by `TextOverflowDirective`, never counted from the string.
   *
   * Latched: opening the panel removes the clamp, at which point nothing overflows any more, and
   * an un-latched flag would drop the control for a frame every time the panel closed. Reset per
   * promotion in `load`, which is the only moment the copy can actually change.
   */
  textTruncated = false;

  private advanceTimer: any = null;
  private holdTimer: any = null;
  private didHold = false;
  private pointerActive = false;
  private pointerStartX = 0;
  private pointerStartY = 0;
  /** When the current still's clock started, and what was left of it when a hold froze it. */
  private slideStartedAt = 0;
  private remainingMs = STILL_DURATION_MS;

  /** Which slide is showing. Drives the segment bars, the pills, and which video may have sound. */
  activeIndex = 0;

  /**
   * Sound and crop are the diner's choices, and both are SESSION-SCOPED — held in this instance
   * only. Paging the gallery must not reset them, and neither should be remembered on a later
   * visit: a promotion that made noise on its own the second time round would be indefensible.
   */
  soundOn = false;
  fillLandscape = false;

  /**
   * `prefers-reduced-motion`, read live. It is not a one-off: the setting can change mid-session
   * (a phone entering a battery saver flips it), and every video affordance on this screen has to
   * disappear with it — there is no clip playing to unmute.
   */
  reducedMotion = false;

  /**
   * The real shape of each slide, once the browser has decoded it, keyed by index.
   *
   * Overrides the document's `orientation`, which is metadata and can be wrong — the live
   * landscape-tagged promotion is a 1254x1254 square. Only what the pixels say can settle whether
   * an item needs letterboxing.
   */
  private resolvedOrientation = new Map<number, 'portrait' | 'landscape'>();

  /**
   * Slides whose clip could not play and are showing a still.
   *
   * They stop counting as video everywhere it matters: the sound and crop pills disappear, because
   * there is nothing to unmute or re-crop, and — the reason this exists — the slide goes back on
   * the clock, since a still that is waiting for a video's `ended` waits forever.
   */
  private failedVideo = new Set<number>();

  /** The gallery track, so the segment bars can jump to a slide. */
  @ViewChild('track') trackRef?: ElementRef<HTMLElement & { swiper?: { slideTo(i: number): void } }>;

  /** The bar strip. One element to write the playhead to, rather than one per bar. */
  @ViewChild('bars') barsRef?: ElementRef<HTMLElement>;

  /** Every mounted slide, in template order — so index N here is slide N. */
  @ViewChildren(EventMediaComponent) mediaRefs?: QueryList<EventMediaComponent>;

  /** The running rAF handle for the clip playhead, or null when nothing is being tracked. */
  private progressFrame: number | null = null;

  /** Last value written, so a frame that changed nothing does not touch the DOM. */
  private lastProgress = -1;

  private routeSub?: Subscription;
  private motionQuery?: MediaQueryList;
  private motionListener?: (event: MediaQueryListEvent) => void;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private promotions: PromotionsService,
    private languageService: LanguageService,
    private analytics: AnalyticsService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.readShopFromSession();
    this.watchReducedMotion();

    // paramMap rather than a snapshot, matching the event details screen: a second notification
    // tapped while this screen is open lands on the SAME route with a new id, and Angular reuses
    // the component instead of recreating it. A snapshot read would leave the first promotion on
    // screen for good.
    this.routeSub = this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (!id) {
        this.router.navigate(['/menu']);
        return;
      }
      this.load(id);
    });
  }

  ngOnDestroy(): void {
    this.clearPacing();
    this.stopProgressLoop();
    this.clearHold();
    this.routeSub?.unsubscribe();
    if (this.motionQuery && this.motionListener) {
      this.motionQuery.removeEventListener('change', this.motionListener);
    }
  }

  private async load(promotionId: string): Promise<void> {
    this.loading = true;
    this.notFound = false;
    this.promotion = null;
    this.mediaItems = [];
    this.activeIndex = 0;
    this.resolvedOrientation.clear();
    // Deliberately NOT resetting `soundOn` / `fillLandscape`: they are the diner's session, and a
    // second notification arriving is not a reason to overrule what they already chose.
    window.scrollTo(0, 0);

    const promotion = await this.promotions.getPromotion(promotionId);

    // Rendered even when `active` is false. The only way to be here is holding a notification this
    // promotion already sent, and a shop deactivating it in the window between the send and the tap
    // should not turn the diner's notification into a dead end.
    this.promotion = promotion;
    this.mediaItems = promotionMedia(promotion);
    this.failedVideo.clear();
    this.textExpanded = false;
    this.textTruncated = false;
    this.notFound = !promotion;
    this.loading = false;
    this.restartPacing();

    if (!promotion) return;

    this.analytics.log('promotion_open', {
      content_id: promotionId,
      promotion_type: promotion.type ?? 'unknown',
      // The gallery is mixed, so report what LEADS it: whether a push opens on a video or a still
      // is the part that plausibly changes engagement. Mirrors iOS `logNotificationMediaOpen`.
      media_type: (this.mediaItems[0]?.mediaType ?? 'none').trim().toLowerCase() || 'none',
      media_count: this.mediaItems.length
    });
  }

  /**
   * The shop, from the session the splash screen populated. Absent only if this route were opened
   * without ever passing through splash, which no notification path does — the click URL always
   * goes through `/?id=…` so auth runs first. The chip self-hides rather than showing a placeholder.
   */
  private readShopFromSession(): void {
    try {
      const raw = sessionStorage.getItem('clientData');
      const clientData = raw ? JSON.parse(raw) : null;
      this.shopName = clientData?.name || '';
      this.shopLogoUrl = clientData?.catalogue_icon || '';
    } catch {
      /* A malformed session is not worth failing the screen for. */
    }
  }

  private watchReducedMotion(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion = this.motionQuery.matches;

    this.motionListener = (event: MediaQueryListEvent) => {
      // The listener fires outside Angular, and it flips whether the pills render.
      this.zone.run(() => { this.reducedMotion = event.matches; });
    };
    this.motionQuery.addEventListener('change', this.motionListener);
  }

  // -------------------------------------------------------------------------
  // derived
  // -------------------------------------------------------------------------

  /**
   * Title and body in the diner's CURRENT language, not the language the notification was sent in.
   *
   * The Cloud Function localizes the notification text once, against the language stored on the
   * subscription. Re-localizing here means a diner who has since switched language reads the screen
   * in the one they are using now, and it keeps this screen consistent with the rest of the menu.
   * `localizeMap` applies the same lookup order the function does: exact code, then English, then
   * the shop's default scalar.
   */
  get title(): string {
    return this.languageService.localizeMap(
      this.promotion?.titleTranslations,
      this.promotion?.title ?? ''
    );
  }

  get description(): string {
    return this.languageService.localizeMap(
      this.promotion?.descriptionTranslations,
      this.promotion?.description ?? ''
    );
  }

  /**
   * The dismiss button's label, or empty when the promotion authors none — and empty means NO
   * BUTTON, not a default one.
   *
   * That is iOS's rule verbatim (`if let dismissText, !dismissText.isEmpty`), and it is worth not
   * "improving": the button exists only because an admin wrote a call to action for it. Both live
   * media promotions have `actionButtonText: null`, so on iOS they show no button at all — a web
   * fallback reading "Close" would put a control on the screen that the iOS build does not have.
   * The back arrow is the way out; see the note on it in the template.
   */
  get actionLabel(): string {
    return this.languageService.localizeMap(this.promotion?.actionButtonText, '').trim();
  }

  get isMedia(): boolean {
    return isMediaPromotion(this.promotion);
  }

  get hasMedia(): boolean {
    return this.mediaItems.length > 0;
  }

  /** Multi-item galleries get segment bars and a swipeable track; a single item gets neither. */
  get isGallery(): boolean {
    return this.mediaItems.length > 1;
  }

  get activeItem(): PromotionMediaItem | null {
    return this.mediaItems[this.activeIndex] ?? null;
  }

  /** The blurred plate behind the stage — the active item's own still. */
  get backdropUrl(): string | null {
    return mediaBackdropUrl(this.activeItem);
  }

  /**
   * Whether a clip is actually playing right now, which is what every video control depends on.
   * Under reduced motion nothing plays — the poster stands in — so there is nothing to unmute and
   * nothing to re-crop.
   */
  get activeIsPlayingVideo(): boolean {
    return !this.reducedMotion && isVideoMedia(this.activeItem) && !this.failedVideo.has(this.activeIndex);
  }

  /** "Fill frame" is offered on a landscape CLIP only. A still always letterboxes — see `mediaFitFor`. */
  get canToggleFill(): boolean {
    return this.activeIsPlayingVideo && this.orientationOf(this.activeIndex) === 'landscape';
  }

  /**
   * Whether the footer holds a button. It changes the text block's floor: with a CTA the text sits
   * 16px above it, without one it needs the full home-indicator clearance itself.
   */
  get hasFooter(): boolean {
    return !!this.actionLabel;
  }

  /** The shape this slide is being treated as — decoded pixels first, document metadata second. */
  orientationOf(index: number): 'portrait' | 'landscape' {
    return this.resolvedOrientation.get(index) ?? mediaOrientation(this.mediaItems[index]);
  }

  /** How this item fills the stage. See `mediaFitFor` for why shape decides and not file type. */
  fitFor(index: number): 'cover' | 'contain' {
    const item = this.mediaItems[index];
    if (this.orientationOf(index) !== 'landscape') return 'cover';
    return this.fillLandscape && isVideoMedia(item) ? 'cover' : 'contain';
  }

  /**
   * Only the slide on screen may make noise, and only once the diner has asked for it. Every other
   * slide stays muted — a gallery that played three soundtracks at once would be a bug the mute
   * control could not fix.
   */
  mutedFor(index: number): boolean {
    return !this.soundOn || index !== this.activeIndex;
  }

  // -------------------------------------------------------------------------
  // actions
  // -------------------------------------------------------------------------

  /**
   * Jump to a slide from its segment bar.
   *
   * Reaches for Swiper's own instance on the element rather than tracking position ourselves: the
   * track is also draggable, and two sources of truth for "which slide" is how a carousel ends up
   * showing one item while its indicator points at another.
   */
  goTo(index: number): void {
    this.trackRef?.nativeElement?.swiper?.slideTo(index);
    // A single item renders no track at all, so nothing above can run — but the bars only exist on
    // a gallery, so that case cannot reach here.
  }

  /** Swiper's `activeIndex`, off the web component's own event. */
  onSlideChange(event: Event): void {
    const swiper = (event as CustomEvent).detail?.[0];
    const index = swiper?.activeIndex;
    if (typeof index === 'number' && index !== this.activeIndex) {
      this.activeIndex = index;
      // However the slide changed — tap, swipe, or the clock — the new one starts its own.
      this.restartPacing();
    }
  }

  // -------------------------------------------------------------------------
  // story pacing
  // -------------------------------------------------------------------------

  /**
   * Whether THIS slide is on a clock.
   *
   * A still is; a playing clip is not, because it advances on its own `ended`. Under reduced
   * motion nothing is: an unattended carousel that redraws itself every five seconds is precisely
   * the moving content that setting asks us to stop, and tapping still works for anyone who wants
   * to move on. A single-item promotion has nowhere to advance to.
   */
  get isTimedSlide(): boolean {
    return this.isGallery && !this.reducedMotion && !this.activeIsPlayingVideo;
  }

  /**
   * The mirror of `isTimedSlide`: a slide whose bar is driven by a clip rather than by a clock.
   *
   * Exactly one of the two is true on any running slide — except under reduced motion, where
   * neither is and the bar simply sits lit, because nothing is moving to measure.
   */
  get isLiveSlide(): boolean {
    return this.isGallery && this.activeIsPlayingVideo;
  }

  /** The still's clock, for the bar's fill animation, so CSS and the timer cannot disagree. */
  get stillDurationMs(): number {
    return STILL_DURATION_MS;
  }

  /**
   * Whether anything is holding the story still: a finger on the screen, or the copy open for
   * reading. Two independent reasons, so neither may simply clear the other — releasing a hold
   * while the reading panel is open must not start the clock again.
   */
  get storyHeld(): boolean {
    return this.paused || this.textExpanded;
  }

  private restartPacing(): void {
    this.clearPacing();
    this.resetProgress();
    this.rewindActiveClip();
    this.remainingMs = STILL_DURATION_MS;
    this.paused = false;
    this.syncProgressLoop();
    if (this.isTimedSlide && !this.storyHeld) this.armAdvance(STILL_DURATION_MS);
  }

  /**
   * Bring the clock into line with whatever is holding it.
   *
   * Idempotent on purpose — both hold reasons call it and they overlap freely. The guards are what
   * make that safe: time is only banked off a clock that is actually running, so a second hold
   * cannot charge the slide twice for the same seconds.
   */
  private applyHold(): void {
    // Ahead of the timed-slide guard below: a clip's playhead is held by the same reasons, and it
    // is the case that guard returns early on.
    this.syncProgressLoop();

    if (!this.isTimedSlide) return;

    if (this.storyHeld) {
      if (this.advanceTimer) {
        this.remainingMs = Math.max(this.remainingMs - (Date.now() - this.slideStartedAt), 0);
        this.clearPacing();
      }
    } else if (!this.advanceTimer) {
      this.armAdvance(this.remainingMs);
    }
  }

  /**
   * Send the slide's clip back to its first frame.
   *
   * Every slide in the gallery is mounted at once and only paused when it leaves the viewport, so
   * without this a clip resumes wherever it was abandoned — swipe away four seconds in, come back,
   * and the diner is shown the end of something they never saw the start of. A story slide is not a
   * video that happens to be on screen; arriving at it is arriving at it again.
   *
   * Only the active slide is touched. The others are already paused and will be rewound at the
   * moment they become active, which is the only moment it matters.
   */
  private rewindActiveClip(): void {
    this.mediaRefs?.get(this.activeIndex)?.restart();
  }

  /**
   * A clip's bar tracks the clip, a still's bar tracks a timer.
   *
   * They cannot share a mechanism. A still has no playhead, so its bar is a CSS animation over
   * `STILL_DURATION_MS` — the same number that advances it. A clip has one, and reading it is the
   * only way the bar stays honest through a buffering stall, a slow start, or a codec that decodes
   * at less than real time. A five-second animation laid over a clip that turned out to take seven
   * would sit at full for two seconds, promising a slide change that had not happened.
   */
  private syncProgressLoop(): void {
    if (this.isLiveSlide && !this.storyHeld) this.startProgressLoop();
    else this.stopProgressLoop();
  }

  private startProgressLoop(): void {
    if (this.progressFrame !== null) return;

    // Outside Angular: this runs every frame for the length of a clip and changes no bound state —
    // it writes one custom property straight to the strip. Inside the zone it would be a full
    // change-detection pass per frame, on top of a playing video.
    this.zone.runOutsideAngular(() => {
      const tick = () => {
        const ratio = this.mediaRefs?.get(this.activeIndex)?.progress;

        // Null means metadata has not landed yet (or never will, on a stream). Hold at whatever was
        // last written rather than snapping the bar back to zero underneath the viewer.
        if (ratio !== null && ratio !== undefined) {
          this.writeProgress(ratio);

          // Played out. The clip's `ended` will move the slide; on the last one it will not, and
          // there is nothing further to draw either way.
          if (ratio >= 1) { this.progressFrame = null; return; }
        }

        this.progressFrame = requestAnimationFrame(tick);
      };
      this.progressFrame = requestAnimationFrame(tick);
    });
  }

  private stopProgressLoop(): void {
    if (this.progressFrame === null) return;
    cancelAnimationFrame(this.progressFrame);
    this.progressFrame = null;
  }

  /** Write the playhead, skipping frames that would not move a 3px bar by a visible amount. */
  private writeProgress(ratio: number): void {
    const clamped = Math.min(Math.max(ratio, 0), 1);
    if (Math.abs(clamped - this.lastProgress) < 0.002) return;
    this.lastProgress = clamped;
    this.barsRef?.nativeElement.style.setProperty('--pm-progress', String(clamped));
  }

  /** Back to the start of the strip, for a slide that is about to begin. */
  private resetProgress(): void {
    this.lastProgress = -1;
    this.writeProgress(0);
  }

  private armAdvance(ms: number): void {
    this.clearPacing();
    this.slideStartedAt = Date.now();
    // Outside Angular so a five-second wait is not five seconds of change detection; the callback
    // steps back in, because it moves the slide.
    this.zone.runOutsideAngular(() => {
      this.advanceTimer = setTimeout(() => this.zone.run(() => this.next()), ms);
    });
  }

  private clearPacing(): void {
    if (this.advanceTimer) {
      clearTimeout(this.advanceTimer);
      this.advanceTimer = null;
    }
  }

  /**
   * A clip played to its end.
   *
   * Guarded on the index because every slide in the gallery is mounted at once: the clip two
   * slides along finishing off-screen must not drag the diner forward.
   */
  onMediaEnded(index: number): void {
    if (index !== this.activeIndex) return;
    this.next();
  }

  /** A clip fell back to its still, so put the slide on the clock it was not going to need. */
  onMediaFailed(index: number): void {
    if (this.failedVideo.has(index)) return;
    this.failedVideo.add(index);
    if (index === this.activeIndex) this.restartPacing();
  }

  /** The last slide holds. A promotion that closed itself would take its own call to action with it. */
  next(): void {
    if (this.activeIndex >= this.mediaItems.length - 1) {
      this.clearPacing();
      return;
    }
    this.goTo(this.activeIndex + 1);
  }

  /** Instagram's rule: back past the first slide restarts it rather than leaving the promotion. */
  prev(): void {
    if (this.activeIndex <= 0) {
      this.restartPacing();
      return;
    }
    this.goTo(this.activeIndex - 1);
  }

  // -------------------------------------------------------------------------
  // gestures
  // -------------------------------------------------------------------------

  /**
   * Press: start the hold timer that freezes the story, and remember where the finger landed.
   *
   * Bound on the stage rather than on a tap layer of its own, which is what the reel uses. The
   * reel has no carousel under it; this screen does, and an overlay would swallow the drag Swiper
   * needs. Letting the events run their normal course leaves swipe working untouched and reads
   * the tap on the way past.
   */
  onStagePointerDown(ev: PointerEvent): void {
    // With the copy open there is nothing to hold — the story is already held — and the panel owns
    // its own scrolling. `onStagePointerUp` still runs, to close on a tap outside it.
    if (this.textExpanded) return;
    if (!this.isGallery) return;
    if ((ev.target as HTMLElement | null)?.closest(CHROME_SELECTOR)) return;

    this.pointerActive = true;
    this.didHold = false;
    this.pointerStartX = ev.clientX;
    this.pointerStartY = ev.clientY;
    this.clearHold();
    this.zone.runOutsideAngular(() => {
      this.holdTimer = setTimeout(() => {
        this.zone.run(() => {
          this.didHold = true;
          this.setPaused(true);
        });
      }, HOLD_THRESHOLD_MS);
    });
  }

  /** Release: resume from a hold, ignore a swipe, otherwise navigate by which third was tapped. */
  onStagePointerUp(ev: PointerEvent): void {
    // Tap-away closes the reading panel, and never also moves the story: someone dismissing a wall
    // of text is not asking for the next slide.
    if (this.textExpanded) {
      if (!(ev.target as HTMLElement | null)?.closest('.pm-text')) this.collapseText();
      return;
    }

    if (!this.pointerActive) return;
    this.pointerActive = false;
    this.clearHold();

    if (this.didHold) {
      this.didHold = false;
      this.setPaused(false);
      return;
    }

    const dx = Math.abs(ev.clientX - this.pointerStartX);
    const dy = Math.abs(ev.clientY - this.pointerStartY);
    if (dx > TAP_SLOP_PX || dy > TAP_SLOP_PX) return; // a swipe; Swiper owns it

    const rect = (ev.currentTarget as HTMLElement | null)?.getBoundingClientRect();
    const frac = rect && rect.width > 0 ? (ev.clientX - rect.left) / rect.width : 0.5;
    if (frac > TAP_NEXT_MIN) this.next();
    else if (frac < TAP_PREV_MAX) this.prev();
    // The middle third does nothing, as in the reel: it is where the title and the body sit, and
    // a diner reading them must be able to rest a thumb on the screen without losing the slide.
  }

  onStagePointerCancel(): void {
    if (!this.pointerActive) return;
    this.pointerActive = false;
    this.clearHold();
    if (this.didHold) {
      this.didHold = false;
      this.setPaused(false);
    }
  }

  private setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    this.applyHold();
  }

  // -------------------------------------------------------------------------
  // long copy
  // -------------------------------------------------------------------------

  /** The reading panel's ceiling, bound as a style so CSS and the comment cannot drift apart. */
  get readingPanelMaxHeight(): string {
    return READING_PANEL_MAX_VH + '%';
  }

  /**
   * The clamped copy reported whether it is hiding anything.
   *
   * Only ever latches ON: see `textTruncated` for why an honest false would flicker the control.
   */
  onTextOverflow(overflows: boolean): void {
    if (overflows) this.textTruncated = true;
  }

  expandText(): void {
    if (this.textExpanded) return;
    this.textExpanded = true;
    this.applyHold();
    this.analytics.log('promotion_text_expanded', {
      content_id: this.promotion?.id ?? '',
      media_count: this.mediaItems.length
    });
  }

  collapseText(): void {
    if (!this.textExpanded) return;
    this.textExpanded = false;
    this.applyHold();
  }

  private clearHold(): void {
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }

  /** A slide reported its decoded shape, which may disagree with the document. */
  onAspectResolved(index: number, orientation: 'portrait' | 'landscape'): void {
    if (this.resolvedOrientation.get(index) === orientation) return;
    this.resolvedOrientation.set(index, orientation);
  }

  toggleSound(): void {
    this.soundOn = !this.soundOn;
    this.analytics.log('promotion_sound_toggled', {
      content_id: this.promotion?.id ?? '',
      sound_on: String(this.soundOn)
    });
  }

  toggleFill(): void {
    this.fillLandscape = !this.fillLandscape;
    this.analytics.log('promotion_fit_toggled', {
      content_id: this.promotion?.id ?? '',
      fill_frame: String(this.fillLandscape)
    });
  }

  /**
   * The document's `actionType` is "close" on every live promotion and dismissing is the only
   * behaviour iOS implements, so the button and the back arrow do the same thing. Kept as one
   * method rather than branching on a field with a single value.
   */
  onAction(): void {
    this.back();
  }

  back(): void {
    // A notification tap is a deep link, so on a cold start there is nothing behind this screen and
    // `location.back()` would leave the site entirely. The menu is the right floor: the diner came
    // from this venue's QR code. Same guard as the event details screen.
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['/menu']);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
}
